import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconBack } from '../components/Icon';
import { setFeatured, useAllFeatured, useMediaPolling } from '../lib/media';
import type { MediaItem } from '../lib/media';
import { useSession } from '../lib/session';
import { useOnline } from '../lib/online';

/**
 * Artboards 1n (before fullscreen) and 1m (running) — the reception slideshow,
 * one page in two states. The setup panel is the only chrome; after Start there
 * is nothing else.
 *
 * The uncuttable one (D29). Nobody will be watching it to fix problems, so the
 * rules are about what it must never do:
 *   · never put an error on the wall — a failed refresh keeps cycling what is
 *     already in memory, silently;
 *   · never show a blank screen — an empty manifest gets a holding card;
 *   · never autoplay a video, because videos are not in it at all (D26).
 */

/**
 * How long each photo is up, crossfade included: 7 s, so about 5 s fully on
 * screen and 2 s fading into the next (`.kiosk-slide`'s transition). It was
 * 9 s until 20 Oct, which felt slow on the wall.
 */
export const SLIDE_MS = 7000;
const NO_REPEAT_WINDOW = 0.6;

/**
 * How much of the deck stays mounted around the photo on screen.
 *
 * **A mounted slide downloads immediately.** `.kiosk-slide` is `opacity: 0`, not
 * `display: none`, so the browser fetches and decodes every slide it is given
 * whether or not the room can see it. Rendering the whole deck asked the resort
 * link for six hundred 2560px display copies at once, and grew on every 15 s
 * poll. Nothing on screen showed it: the current slide is requested first, so
 * the show starts normally and degrades as the queue behind it fills.
 *
 * **Eleven, not four.** One laptop on mains power: ~11 MB in flight is nothing
 * to it. What matters is that the deck stays *bounded*, not the number.
 *
 * `AHEAD` buys lead time — at the 260 kB/s §8 models for the resort a ~800 kB
 * copy takes ~3 s, and five slides at 7 s each is 35 s of runway. It is also
 * most of the offline story until the bounded pre-cache lands.
 *
 * `BEHIND` is as generous, not for going backwards but because the deck is
 * reshuffled and re-indexed at every lap boundary, and slides that stay mounted
 * across that do not have to be fetched again.
 */
const BEHIND = 5;
const AHEAD = 5;

/**
 * The indices to mount, wrapping at both ends.
 *
 * **Returned in fetch order, not deck order, and that is load-bearing.** The
 * browser requests images roughly in insertion order, so a positional window put
 * the five `BEHIND` slides *before* the one the room is waiting for — at index 0
 * that is `[595…599, 0, 1…5]`, five megabytes queued ahead of the photograph
 * that gates the first frame. On a 260 kB/s link that is three seconds of dark
 * wall against about twenty.
 *
 * So: current first, then forwards, then behind — also the order they are needed
 * in. `BEHIND` buys nothing at index 0 and only pays at a lap boundary.
 *
 * Exported for the test: it is pure, and the alternative is asserting on opacity
 * through jsdom, which does not run transitions.
 */
export function windowIndices(
  index: number, length: number, behind = BEHIND, ahead = AHEAD,
): number[] {
  if (length <= 0) return [];
  const at = (offset: number) => ((index + offset) % length + length) % length;

  // Fewer photos than the window is normal for the first hour. Mount them all,
  // still current-first, without repeating an index.
  const span = behind + ahead + 1;
  const reach = length <= span ? length - 1 : ahead;

  const out = [at(0)];
  for (let offset = 1; offset <= reach; offset += 1) out.push(at(offset));
  if (length <= span) return out;
  for (let offset = 1; offset <= behind; offset += 1) out.push(at(-offset));
  return out;
}

/**
 * Which slides are in the DOM, and **in what order they stay there**.
 *
 * Pure and exported for the same reason `windowIndices` is: the invariant it
 * carries cannot be asserted through jsdom, which runs no transitions and lays
 * nothing out. The invariant is that **a photograph already mounted never
 * changes position relative to another already-mounted one.** React reconciles
 * a reordered list by moving nodes with `insertBefore`, which takes the node
 * out of the document and puts it back, and that cancels any transition
 * running on it — so a reorder on the commit that swaps `.on` turns a two
 * second fade into a hard cut.
 *
 * Measured before the fix, sampling composite coverage every 50ms in a browser:
 * **~1s of an entirely black projector on every slide change**, 20 of 427
 * frames at zero. A MutationObserver caught the showing node travelling from
 * DOM index 0 to 6 on the commit that swapped `.on`.
 *
 * Sorting by position in the deck gives that: as the window advances one photo
 * is inserted and another removed, and every survivor keeps its place. Pinned
 * photographs — the ones the deck no longer holds at all — sort ahead of
 * everything and stay there.
 *
 * DOM order therefore says nothing about which photograph is on top any more.
 * That was never a good place to keep it; `.kiosk-slide` now carries an
 * explicit `z-index`. Download priority, the other thing the old order carried,
 * moved to `fetchPriority` on the image.
 */
/**
 * How hard the browser should chase each mounted photograph.
 *
 * `windowIndices` used to carry this in its shape — current first, then ahead,
 * then behind, "also the order they are needed in" — and the crossfade fix took
 * that away by sorting the DOM on deck position instead. This says it directly,
 * which is better than a rendering order having to mean two things at once.
 *
 * Distance forward round the deck, so it does not depend on how `windowIndices`
 * happens to lay its result out: the photograph on screen is what the fade is
 * waiting for, the ones just ahead are needed within the runway `AHEAD` buys,
 * and the ones behind are not needed again until the next lap.
 */
export function slidePriority(
  order: readonly { id: string }[], index: number, id: string,
): 'high' | 'auto' | 'low' {
  const at = order.findIndex((it) => it.id === id);
  // Pinned photographs are off the deck entirely; they are already on screen or
  // just leaving it, and nothing is waiting on their bytes.
  if (at < 0 || order.length === 0) return 'low';
  const ahead = ((at - index) % order.length + order.length) % order.length;
  if (ahead === 0) return 'high';
  return ahead <= AHEAD ? 'auto' : 'low';
}

export function mountedSlides<T extends { id: string }>(
  order: readonly T[], index: number, shown: T | null, outgoing: T | null,
): T[] {
  const picked = windowIndices(index, order.length).map((i) => order[i]!);
  const byId = new Set(picked.map((it) => it.id));
  // Pinned whether or not the window still holds them: the deck can be
  // reshuffled out from under both, and unmounting either is the cut this
  // arrangement exists to avoid.
  const pinned = [outgoing, shown].filter(
    (it): it is T => !!it && !byId.has(it.id),
  );
  const place = new Map(order.map((it, i) => [it.id, i]));
  // `-1` for a pinned photo the deck no longer holds, so it sorts ahead of
  // everything and cannot shuffle around as the window moves past it.
  return [...pinned, ...picked].sort(
    (a, b) => (place.get(a.id) ?? -1) - (place.get(b.id) ?? -1),
  );
}

function shuffle<T>(input: T[]): T[] {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * The frame around a photo: its sampled colour (D72), or nothing, which leaves
 * the stylesheet's neutral dark. Checked, not trusted, because it goes into a
 * `style` — anything but `#rrggbb` is dropped rather than painted.
 */
export const frame = (tint: string | undefined) =>
  (tint && /^#[0-9a-f]{6}$/.test(tint) ? { backgroundColor: tint } : undefined);

export default function Display() {
  const navigate = useNavigate();
  // The whole FEATURED list, not its first page: the deck is a shuffle of
  // everything on the wall, so a partial pool is not a shorter show but
  // photographs that never go up, indistinguishable from the shuffle not having
  // reached them. Under D51 this walks one sparse cursor rather than every page
  // of the week.
  const media = useAllFeatured();
  // The projector's job is showing what just happened, so it polls more often
  // than the gallery. D29: this screen must never stop.
  useMediaPolling(15_000);
  const session = useSession();
  const online = useOnline();
  const [started, setStarted] = useState(false);
  const [index, setIndex] = useState(0);
  const [wakeHint, setWakeHint] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Photos only, and only what earned the wall (D51). A featured video shows in
  // the Featured tab and never here (D26).
  const pool = useMemo(
    () => media.filter((it) => it.kind === 'photo' && it.featured && it.state === 'done'),
    [media],
  );

  const [order, setOrder] = useState<MediaItem[]>([]);

  useEffect(() => {
    setOrder((prev) => {
      // Keep the deck already playing; fold new arrivals in rather than
      // reshuffling under the room's feet.
      const fresh = new Map(pool.map((it) => [it.id, it]));
      const known = new Set(prev.map((it) => it.id));
      const added = pool.filter((it) => !known.has(it.id));

      /**
       * **Take the poll's row, not the one already in the deck.** What this
       * effect preserves is a photo's *position*, not its object.
       *
       * `src` is a presigned S3 GET (D49), good for six hours. Keeping `prev`'s
       * objects froze each URL at the moment it entered the deck, harmless only
       * while the whole deck stayed mounted and every image was fetched once.
       * With a bounded window a photo is re-fetched every time it re-enters, so
       * a frozen URL becomes a 403 — and an `<img>` that 403s renders nothing:
       * black frame, caption band still up, nothing in any log. Six hours is an
       * evening, so it would have started mid-reception.
       *
       * This refreshes what a poll returned, which is the head page; a deeper
       * deck still holds older URLs. Re-signing the tail belongs with the
       * bounded pre-cache in §8 of the todo.
       */
      const kept = prev.filter((it) => fresh.has(it.id)).map((it) => fresh.get(it.id)!);
      if (kept.length === 0) return shuffle(pool);
      return added.length ? [...kept, ...shuffle(added)] : kept;
    });
  }, [pool]);

  const advance = useCallback(() => {
    setIndex((i) => {
      if (order.length === 0) return 0;
      const next = i + 1;
      if (next < order.length) return next;
      // Reshuffle at the end, keeping the tail out of the front so the room
      // does not see the same photo twice in a row.
      const recent = new Set(order.slice(Math.floor(order.length * NO_REPEAT_WINDOW)).map((it) => it.id));
      setOrder((prevOrder) => {
        const reshuffled = shuffle(prevOrder);
        const head = reshuffled.filter((it) => !recent.has(it.id));
        const tail = reshuffled.filter((it) => recent.has(it.id));
        return [...head, ...tail];
      });
      return 0;
    });
  }, [order]);

  useEffect(() => {
    if (!started || order.length === 0) return;
    const id = setInterval(advance, SLIDE_MS);
    return () => clearInterval(id);
  }, [started, advance, order.length]);

  // Screen Wake Lock, requested once. A refusal is told to the operator before
  // fullscreen and never after.
  useEffect(() => {
    let lock: WakeLockSentinel | null = null;
    const nav = navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinel> } };
    nav.wakeLock?.request('screen').then((l) => { lock = l; }).catch(() => setWakeHint(true));
    if (!nav.wakeLock) setWakeHint(true);
    return () => { void lock?.release(); };
  }, []);

  const current = order[index];

  /**
   * **What the room is looking at, and it is not necessarily `order[index]`.**
   * The index moves on a timer; a photograph appears when its bytes arrive, and
   * tying the two together put black frames on the wall: a slide mounted and
   * given `.on` in the same commit has no previous computed opacity to
   * transition from, so it snaps — to nothing, if the image has not arrived,
   * with the caption band sitting over it. That happened at every lap boundary,
   * where the reshuffle hands the window a photo it has never held.
   *
   * So the fade is driven by the image, not the clock: `shown` only moves once
   * that slide's bytes have loaded, so the previous photo stays up until the
   * next is genuinely ready — and going offline degrades into holding the last
   * loaded photograph rather than cutting to black.
   *
   * `outgoing` is the previous `shown`, set in the same update, so both are
   * mounted on the commit that swaps `.on` between them. That is the whole
   * requirement for a CSS crossfade.
   */
  const [loaded, setLoaded] = useState<ReadonlySet<string>>(new Set());
  const [shown, setShown] = useState<MediaItem | null>(null);
  const [outgoing, setOutgoing] = useState<MediaItem | null>(null);

  // Keyed on the URL, not the photo id: a poll re-signs `src` (D49) and a
  // re-signed URL has not been fetched yet, so keying on the id would call a
  // refreshed row loaded and show it before its bytes existed.
  //
  // Consequence worth knowing: D49 pins signatures to a one-hour window, so at
  // each hour boundary all eleven mounted rows get new URLs, drop out of this
  // set and re-download. Self-healing, degrading to one extra slide on the
  // current photo.
  const markLoaded = useCallback((src: string) => {
    setLoaded((prev) => (prev.has(src) ? prev : new Set(prev).add(src)));
  }, []);

  useEffect(() => {
    if (!current || !loaded.has(current.src)) return;
    if (shown?.id === current.id) return;
    setOutgoing(shown);
    setShown(current);
  }, [current, loaded, shown]);

  /**
   * **A photo that leaves the deck leaves the wall, immediately.**
   *
   * `shown` is otherwise only moved by the effect above, which waits for the
   * next slide's bytes, and `mounted` pins `shown` precisely so the deck cannot
   * unmount it. Together those meant taking the photo on screen off the wall
   * removed it from the deck while it carried on being projected, until some
   * other slide happened to load. Usually instant, because five ahead are
   * preloaded — but the case where it is not is exactly the degraded link this
   * screen advertises holding through, and it would hold on the photo the
   * organiser just removed while the toast said it was gone.
   *
   * Clearing it is enough: the deck is already reindexed, so the effect above
   * puts the next loaded photo up on the following commit, and the holding card
   * covers the gap if none is ready.
   */
  useEffect(() => {
    const live = new Set(order.map((it) => it.id));
    if (shown && !live.has(shown.id)) setShown(null);
    if (outgoing && !live.has(outgoing.id)) setOutgoing(null);
  }, [order, shown, outgoing]);

  const mounted = useMemo(
    () => mountedSlides(order, index, shown, outgoing),
    [order, index, shown, outgoing],
  );

  /**
   * Forget the load state of anything unmounted, so this cannot grow into a
   * record of every photograph of the week. Returning `prev` unchanged when
   * nothing was dropped is what stops it re-rendering in a loop.
   */
  useEffect(() => {
    const live = new Set(mounted.map((it) => it.src));
    setLoaded((prev) => {
      const kept = [...prev].filter((src) => live.has(src));
      return kept.length === prev.size ? prev : new Set(kept);
    });
  }, [mounted]);

  /**
   * Back to the setup panel. Without it there was no way out: Start goes
   * fullscreen with `cursor: none` and nothing to click, and Escape drops the
   * browser out of fullscreen while the app carries on believing it is running
   * — a windowed slideshow with an invisible cursor and no exit.
   */
  const exit = useCallback(async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
    } catch {
      /* Already out, or never in. Either way `started` is what matters. */
    }
    setStarted(false);
  }, []);

  // The hotkey sits where a problem becomes visible, with the person running
  // the screen. It needs an admin session in this browser: the display URL
  // alone cannot change what the room sees.
  useEffect(() => {
    if (!started) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { void exit(); return; }
      if (e.key.toLowerCase() !== 'h' || !shown) return;
      if (!session?.isAdmin) {
        setToast('Sign in as an organiser on this laptop to take anything off the wall.');
        setTimeout(() => setToast(null), 4000);
        return;
      }
      // Same key, new verb (D37 as amended by D50): off the wall, not out of
      // the week. It stays in the gallery, and the lock this leaves means three
      // further likes cannot put it back.
      setFeatured(shown.id, false);
      setToast('Off the wall. It is still in the gallery.');
      setTimeout(() => setToast(null), 3000);
      advance();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [started, shown, session, advance, exit]);

  // Escape is what anyone presses first, and the browser eats it to leave
  // fullscreen without telling the app. The other half of `exit`.
  useEffect(() => {
    const onChange = () => { if (!document.fullscreenElement) setStarted(false); };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  async function start() {
    try {
      await rootRef.current?.requestFullscreen?.();
    } catch {
      /* Fullscreen is a nicety. If the browser refuses it, the show still runs. */
    }
    setStarted(true);
  }

  return (
    <div className="kiosk" ref={rootRef} style={{ cursor: started ? 'none' : 'auto' }}>
      {/* Rendered whenever there is a deck, even before any can be shown: they
          have to be in the DOM to download. The card below covers the gap.

          Inside a stage, which exists purely to keep the slides' stacking to
          themselves — see `.kiosk-stage`. Without it the caption band, the
          holding card and the setup panel all render underneath the photo. */}
      <div className="kiosk-stage">
      {mounted.map((item) => (
        <div
          key={item.id}
          className={`kiosk-slide${item.id === shown?.id ? ' on' : ''}${
            item.id === outgoing?.id && item.id !== shown?.id ? ' leaving' : ''}`}
          style={frame(item.tint)}
          aria-hidden={item.id !== shown?.id}
        >
          {/* The whole photo over a flat colour sampled from it (D72), so a
              portrait is never cropped to a landscape screen and the space
              either side reads as a frame. It was the same photo blurred,
              which shimmered on the projector in Chrome and Firefox alike;
              see `.kiosk-slide`. */}
          {/* `onLoad` on the photo: this is the element the room is waiting on.

              `fetchPriority` carries what the array's old current-first order
              used to, and carries it properly: the photograph on screen is what
              the fade is waiting for, the ones just ahead are prefetch, and the
              ones behind are not wanted again until the next lap. Saying it here
              leaves the DOM order free to stay stable, which is what keeps the
              transition from being cancelled. See `slidePriority`. */}
          <img
            className="shot" src={item.src} alt=""
            fetchPriority={slidePriority(order, index, item.id)}
            onLoad={() => markLoaded(item.src)}
          />
        </div>
      ))}
      </div>

      {/**
        * **Gated on `!shown`, not on an empty deck.** Once the fade waits for
        * bytes there are two ways to have nothing to show, and a deck that has
        * not loaded its first photo used to fall through, leaving the room
        * looking at near-black and a monogram — the void §7 of the frontend
        * requirements calls the one unacceptable failure.
        *
        * It sits over the slides rather than replacing them (`position:
        * absolute; inset: 0`), because replacing them would unmount the very
        * images whose arrival clears it.
        */}
      {!shown && (
        <div className="kiosk-hold">
          <div>
            <div className="brand">M&amp;V</div>
            <p style={{ color: '#dcd3c4', fontSize: 18, marginTop: 12 }}>
              {order.length === 0
                ? 'Nothing has been uploaded yet. The first photo of the night lands here.'
                : 'Warming up — the first photo is on its way.'}
            </p>
          </div>
        </div>
      )}

      <div className="kiosk-mono">M&amp;V</div>

      {/* D38: translucent grey band, white centred sans-serif, fading away
          entirely on a photo with no caption rather than sitting empty. */}
      <div className={`kiosk-band${shown?.caption ? ' on' : ''}`}>
        <div className="cap">{shown?.caption}</div>
        <div className="by">{shown?.uploaderName}</div>
      </div>

      {toast && <div className="kiosk-toast">{toast}</div>}

      {!started && (
        <div className="kiosk-setup">
          <div className="kiosk-preview">
            {/* Framed as a running slide is, so the operator sees what the
                room will: a portrait whole, on its colour. A single `cover`
                image here cropped every portrait to its middle, then showed it
                whole once Start was pressed. */}
            {(shown ?? current) ? (
              <div className="kiosk-preview-frame" style={frame((shown ?? current)!.tint)}>
                <img className="shot" src={(shown ?? current)!.src} alt="" />
              </div>
            ) : (
              /* Say so rather than filling it: a generated abstract here made
                 the screen look loaded on an empty pool, which is the one thing
                 the operator needs to know before starting. */
              <div className="kiosk-preview-hold">
                <div className="brand">M&amp;V</div>
                <p>No photos yet. The first upload of the night appears here.</p>
              </div>
            )}
            {(shown ?? current)?.caption && (
              <div className="kiosk-band on" style={{ padding: '22px 34px' }}>
                <div className="cap" style={{ fontSize: 24 }}>{(shown ?? current)!.caption}</div>
              </div>
            )}
          </div>
          <div className="kiosk-bar">
            <button
              type="button" className="kiosk-back" onClick={() => navigate('/now')}
              aria-label="Back to the app"
            >
              <IconBack size={16} /> Back
            </button>
            <div className="brand">M&amp;V</div>
            <div className="facts">
              <span>{pool.length} {pool.length === 1 ? 'photo' : 'photos'} ready</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span className={`kiosk-dot${online ? '' : ' off'}`} />
                {/* "It keeps playing what it has" was true only by accident,
                    while the whole deck was mounted. A bounded window holds five
                    slides of lead, so the honest version is a while, then a
                    hold. §8's bounded pre-cache would earn the old line back. */}
                {online ? 'Connected' : 'Offline — a few more slides, then it holds on the last one'}
              </span>
              <span>Press <strong>H</strong> to hide whatever is on screen</span>
              {/* Escape is the only way out and there is deliberately nothing
                  on screen to click, so here is the only place to say it. */}
              <span>Press <strong>Esc</strong> to come back here</span>
              {!session?.isAdmin && <span style={{ color: '#f6a06b' }}>Sign in as an organiser first to use it</span>}
              {wakeHint && <span style={{ color: '#f6a06b' }}>This browser won't hold the screen awake — turn off sleep in system settings</span>}
            </div>
            <button type="button" className="btn btn-primary" style={{ minHeight: 48, paddingInline: 26, fontSize: 16 }} onClick={start}>
              Start the slideshow
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
